import {
  type KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import type { FormErrors } from "redux-form";
import { SubmissionError } from "redux-form";
import { skipToken } from "@reduxjs/toolkit/query";

import { Typography } from "@braintrust/braintrust-ui-components";
import { fetchCurrentUser } from "@js/apps/auth/actions";
import type { RegisterFormPrefill } from "@js/apps/auth/utils";
import {
  LOGIN_FLOW_PARAM,
  LOGIN_FLOWS,
  REGISTER_FLOW_PARAM,
  REGISTER_FLOWS,
} from "@js/apps/auth/utils";
import { useUser } from "@js/apps/common/hooks";
import { fetchFreelancerProfile } from "@js/apps/freelancer/actions";
import type { CreatePublicFreelancerBidResponse } from "@js/apps/freelancer/api";
import {
  useCreateFreelancerBidMutation,
  useCreatePublicFreelancerBidMutation,
} from "@js/apps/freelancer/api";
import { proposalSubmitted } from "@js/apps/jobs/actions";
import { useGetJobQuery } from "@js/apps/jobs/api";
import {
  closeBidCreateSuccessModal,
  openBidCreateSuccessModal,
} from "@js/apps/jobs/components/bid-create-success-modal";
import type { OpenBidCreateSuccessModalArg } from "@js/apps/jobs/components/bid-create-success-modal/types";
import { openModalAndWaitForInput } from "@js/components/modal";
import { Snackbar } from "@js/components/snackbar";
import { WARNING_MODAL_TYPE } from "@js/forms/hooks/unsaved-changes-warning";
import { getValuesWithReCaptchaCode } from "@js/forms/utils";
import {
  useAppDispatch,
  useAppSelector,
  useGoBackHistory,
  useNavigate,
} from "@js/hooks";
import { useIdParam } from "@js/hooks/use-id-param";
import {
  ConversionEvents,
  pushConversionEvent,
} from "@js/services/analytics/conversion-tracking";
import type { FreelancerBid, FreelancerBidParams } from "@js/types/jobs";
import { getUTMQueryParams } from "@js/utils/url";

import type {
  FreelancerBidFormValues,
  PublicFreelancerBidFormValues,
} from "../../utils/bid-form";
import {
  parseBidFormError,
  parseCreateBidFormValues,
} from "../../utils/bid-form";

const useCreateBidCommon = ({ goBackUrl }: { goBackUrl: string }) => {
  const jobId = useIdParam();
  const {
    currentData: job,
    isFetching,
    refetch,
  } = useGetJobQuery(jobId ? { id: jobId } : skipToken);

  const goBack = useGoBackHistory();
  const [wasCalendarModalShown, setWasCalendarModalShown] = useState(false);

  const hasRefetched = useRef(false);

  useEffect(() => {
    return () => {
      closeBidCreateSuccessModal();
    };
  }, []);

  useEffect(() => {
    if (job?.can_bid !== false) {
      return;
    }

    if (hasRefetched.current && !isFetching) {
      goBack(goBackUrl);
    } else {
      void refetch();
      hasRefetched.current = true;
    }
  }, [job, goBack, isFetching, refetch, goBackUrl]);

  const onSubmitWithoutCalendar = useCallback(async () => {
    setWasCalendarModalShown(true);
    try {
      await openModalAndWaitForInput(
        {
          children: (
            <Typography component="p" sx={{ maxWidth: "550px" }}>
              Are you sure you want to proceed without providing your booking
              calendar URL for screening? Many of our clients begin screening by
              selecting applicants who provide their availability. It is simple
              to set up a free account at calendly.com
            </Typography>
          ),
        },
        { confirmText: "Okay" },
      );
    } catch (_) {
      throw new SubmissionError({});
    }
  }, []);

  return {
    wasCalendarModalShown,
    setWasCalendarModalShown,
    job,
    isFetching,
    onSubmitWithoutCalendar,
  };
};
export const useCreateBid = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { wasCalendarModalShown, onSubmitWithoutCalendar, job, isFetching } =
    useCreateBidCommon({ goBackUrl: "/talent/dashboard/my_jobs/my_proposals" });

  const user = useUser();
  const freelancerResume = useAppSelector(
    (state) => state.freelancer.freelancerProfile?.resume,
  );

  const [createFreelancerBid] = useCreateFreelancerBidMutation();

  useEffect(() => {
    return () => {
      closeBidCreateSuccessModal();
    };
  }, []);

  const handleEscClick = useCallback(
    (ev: KeyboardEvent) => {
      if (ev.key === "Escape") {
        navigate("/jobs/");
      }
    },
    [navigate],
  );

  const onSubmit = useCallback(
    async (values: FreelancerBidFormValues) => {
      const parsedValues = parseCreateBidFormValues(values);
      if (!parsedValues["calendar_url"] && !wasCalendarModalShown) {
        await onSubmitWithoutCalendar();
      }

      return createFreelancerBid(parsedValues)
        .unwrap()
        .then((response) => {
          dispatch(proposalSubmitted(response));
          pushConversionEvent(ConversionEvents.APPLICATION_SUBMIT, {
            transaction_id: response.id,
            new_customer: false,
          });
          return response;
        })
        .catch((error) => {
          const parsedError = parseBidFormError(error);
          const generalError = parsedError?._error;
          if (generalError) {
            Snackbar.error(
              String(parsedError?._error) ||
                "You can not submit an application for this job",
            );
            throw new SubmissionError({});
          }

          throw new SubmissionError(parsedError as FormErrors);
        });
    },
    [
      wasCalendarModalShown,
      createFreelancerBid,
      onSubmitWithoutCalendar,
      dispatch,
    ],
  );

  const onSubmitSuccess = useCallback(
    async (application: FreelancerBid) => {
      if (!user?.freelancer_can_use_job_related_features) {
        await dispatch(fetchCurrentUser());
      }

      if (user?.freelancer) {
        await dispatch(fetchFreelancerProfile(user.freelancer));
      }

      const postApplicationProps: OpenBidCreateSuccessModalArg = {
        onKeyDown: handleEscClick,
        roleId: application.job?.role?.id,
        isScaleAiBid: !!application.is_skipped_approval_bid,
      };

      openBidCreateSuccessModal(postApplicationProps);
    },
    [user, handleEscClick, dispatch],
  );

  const initialValues = useMemo(() => {
    if (!job) return {};

    return {
      payment_amount: job.job_type === ENUMS.JobType.GRANT ? null : undefined,
      calendar_url: user?.calendar_link,
      job: job.id,
      new_application_answers: job.application_questions?.map((question) => {
        return {
          ...question,
          application_question: question.id,
          answer: undefined,
        };
      }),
      resume: freelancerResume ?? null,
      timezone_requirement_met: job.timezone_required ? null : undefined,
      location_requirement_met: job.locations_strongly_required
        ? null
        : undefined,
    } as FreelancerBidParams;
  }, [user, job, freelancerResume]);

  return {
    job,
    loading: isFetching,
    onSubmit,
    onSubmitSuccess,
    initialValues,
    unsavedChangesWarning: true,
    unsavedChangesWarningModelType: WARNING_MODAL_TYPE.NEW_BID,
  };
};

type SubmitSuccessArg = {
  response: CreatePublicFreelancerBidResponse;
  bidValues: PublicFreelancerBidFormValues;
};

export const useCreateBidPublic = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { search } = useLocation();

  const [createPublicFreelancerBid] = useCreatePublicFreelancerBidMutation();

  const { wasCalendarModalShown, onSubmitWithoutCalendar, job, isFetching } =
    useCreateBidCommon({ goBackUrl: "/jobs/" });

  const onSubmit = useCallback(
    async (
      values: PublicFreelancerBidFormValues,
    ): Promise<SubmitSuccessArg> => {
      const parsedValues = parseCreateBidFormValues(values);
      if (!parsedValues["calendar_url"] && !wasCalendarModalShown) {
        await onSubmitWithoutCalendar();
      }

      const valuesWithReCaptchaCode = await getValuesWithReCaptchaCode(
        parsedValues,
        ENUMS.CaptchaActions.PUBLIC_APPLICATION,
      );

      const utmQueryParams = getUTMQueryParams(search);

      const createBidResponse = await createPublicFreelancerBid({
        ...valuesWithReCaptchaCode,
        query_params: utmQueryParams,
      })
        .unwrap()
        .then((response) => {
          dispatch(proposalSubmitted(response));
          pushConversionEvent(ConversionEvents.APPLICATION_SUBMIT, {
            transaction_id: response.id,
            new_customer: !response.user_already_registered,
          });
          return response;
        })
        .catch((error) => {
          const parsedError = parseBidFormError(error);
          const generalError = parsedError?._error;
          if (generalError) {
            Snackbar.error(
              String(parsedError?._error) ||
                "You can not submit an application for this job",
            );
            throw new SubmissionError({});
          }

          throw new SubmissionError(parsedError as FormErrors);
        });

      return { response: createBidResponse, bidValues: parsedValues };
    },
    [
      wasCalendarModalShown,
      createPublicFreelancerBid,
      onSubmitWithoutCalendar,
      dispatch,
      search,
    ],
  );

  const onSubmitSuccess = useCallback(
    ({ response, bidValues }: SubmitSuccessArg) => {
      if (response?.user_already_registered) {
        const params = {
          [LOGIN_FLOW_PARAM]: LOGIN_FLOWS.POST_PUBLIC_APPLICATION,
        };

        const searchParams = new URLSearchParams(params);
        return navigate(`/auth/login?${searchParams.toString()}`);
      }

      const prefilledParams: RegisterFormPrefill = {
        email: bidValues.email,
        first_name: bidValues.first_name,
        last_name: bidValues.last_name,
      };

      const params = {
        ...prefilledParams,
        [REGISTER_FLOW_PARAM]: REGISTER_FLOWS.POST_PUBLIC_APPLICATION,
      };
      const searchParams = new URLSearchParams(params);

      navigate(`/auth/sign_up/?${searchParams.toString()}`);
    },
    [navigate],
  );

  const initialValues = useMemo(() => {
    if (!job) return {};

    return {
      payment_amount: job.job_type === ENUMS.JobType.GRANT ? null : undefined,
      calendar_url: null,
      job: job.id,
      new_application_answers: job.application_questions?.map((question) => {
        return {
          ...question,
          application_question: question.id,
          answer: undefined,
        };
      }),
      resume: null,
      timezone_requirement_met: job.timezone_required ? null : undefined,
      location_requirement_met: job.locations_strongly_required
        ? null
        : undefined,
    } as FreelancerBidParams;
  }, [job]);

  return {
    job,
    loading: isFetching,
    onSubmit,
    onSubmitSuccess,
    initialValues,
    unsavedChangesWarning: true,
    unsavedChangesWarningModelType: WARNING_MODAL_TYPE.NEW_BID,
  };
};
